import {
  Droplets,
  Factory,
  Zap,
  FlaskConical,
  Gauge,
  Wrench,
  SlidersHorizontal,
  Shovel,
  Vault,
  Mountain,
  ClipboardCheck,
  Warehouse,
} from 'lucide-react';

// Three primary market sectors. Each pillar smooth-scrolls to the capabilities
// used within that market (see WhatWeDo.js).
export const MARKETS = [
  {
    id: 'water',
    icon: Droplets,
    label: 'Water',
    title: 'Water Infrastructure',
    headline: 'Built to Deliver.',
    short:
      'The sector OMC was built on in 1999 — the systems that store, treat, and deliver clean water to communities nationwide.',
    description:
      'Municipal water is the sector that OMC was built on in 1999, and it remains the foundation of our business today. We support the systems that store, treat, and deliver clean water to communities across the United States.',
    image: '/markets/water-kearney.jpg?v=2',
    bullets: [
      'Elevated Water Tanks',
      'Ground Storage Tanks',
      'Water Treatment Plants',
      'Pump Stations',
      'Booster Stations',
      'Distribution System Improvements',
    ],
  },
  {
    id: 'industrial',
    icon: Factory,
    label: 'Industrial',
    title: 'Industrial Infrastructure',
    headline: 'Built to Perform.',
    short:
      'Precision construction, rigorous safety, and dependable execution for complex industrial facilities.',
    description:
      'Complex industrial facilities demand precision construction, rigorous safety standards, and dependable execution. OMC provides the specialty civil and process mechanical infrastructure that supports industrial operations from initial site development through project completion.',
    image: '/markets/industrial-pier-field.jpg',
    bullets: [
      'Refining & Petroleum Facilities',
      'Chemical & Petrochemical Plants',
      'Ammonia & Fertilizer Facilities',
      'Manufacturing & Processing Facilities',
      'Military & Government Installations',
      'Bulk Storage & Terminal Facilities',
    ],
  },
  {
    id: 'energy',
    icon: Zap,
    label: 'Energy',
    title: 'Energy Infrastructure',
    headline: 'Built to Power.',
    short:
      'Foundations and complete site construction for water and firewater tanks at natural gas and solar power plants — everything but the tank itself.',
    description:
      'For natural gas and solar power plants, OMC builds the foundations for process water, demineralized water, polished water, and firewater tanks. Once the tank is set, our crews complete the critical process piping, concrete floor pours, and all associated civil and mechanical work — everything but the tank itself — bringing the facility into service.',
    image: '/markets/energy-tes.jpg',
    bullets: [
      'Process Water Tank Foundations',
      'Demineralized & Polished Water Tanks',
      'Firewater Tank Foundations',
      'Critical Process Piping',
      'Concrete Floor Pours & Site Civil',
      'Natural Gas & Solar Power Plants',
    ],
  },
];

// Per-market color tokens (light-background friendly).
export const MARKET_STYLES = {
  water: {
    label: 'Water',
    text: 'text-[hsl(var(--water-blue))]',
    dot: 'bg-[hsl(var(--water-blue))]',
    rule: 'bg-[hsl(var(--water-blue))]',
    tag: 'border-[hsl(var(--water-blue)/0.35)] bg-[hsl(var(--water-blue)/0.08)] text-[hsl(var(--water-blue))]',
    filterActive: 'bg-[hsl(var(--water-blue))] border-[hsl(var(--water-blue))] text-white',
    iconWrap: 'bg-[hsl(var(--water-blue)/0.14)] border-[hsl(var(--water-blue)/0.4)]',
  },
  industrial: {
    label: 'Industrial',
    text: 'text-[hsl(var(--industrial-bronze-3))]',
    dot: 'bg-[hsl(var(--industrial-bronze))]',
    rule: 'bg-[hsl(var(--industrial-bronze))]',
    tag: 'border-[hsl(var(--industrial-bronze)/0.4)] bg-[hsl(var(--industrial-bronze)/0.10)] text-[hsl(var(--industrial-bronze-3))]',
    filterActive: 'bg-[hsl(var(--industrial-bronze))] border-[hsl(var(--industrial-bronze))] text-white',
    iconWrap: 'bg-[hsl(var(--industrial-bronze)/0.16)] border-[hsl(var(--industrial-bronze)/0.45)]',
  },
  energy: {
    label: 'Energy',
    text: 'text-[hsl(var(--energy-green))]',
    dot: 'bg-[hsl(var(--energy-green))]',
    rule: 'bg-[hsl(var(--energy-green))]',
    tag: 'border-[hsl(var(--energy-green)/0.35)] bg-[hsl(var(--energy-green)/0.08)] text-[hsl(var(--energy-green))]',
    filterActive: 'bg-[hsl(var(--energy-green))] border-[hsl(var(--energy-green))] text-white',
    iconWrap: 'bg-[hsl(var(--energy-green)/0.16)] border-[hsl(var(--energy-green)/0.45)]',
  },
};

// Nine specialty disciplines. Each belongs to one or more market sectors.
export const CAPABILITIES = [
  {
    key: 'treatment',
    icon: FlaskConical,
    title: 'Water Treatment & Chemical Feed Systems',
    markets: ['water'],
    desc: 'OMC installs and integrates the process-mechanical systems that treat, disinfect, and deliver clean water. Our crews coordinate closely with manufacturers, engineers, and owners to ensure every treatment system is installed, tested, and commissioned for long-term performance.',
    images: [
      { src: '/capabilities/treatment-hero.jpg', location: 'Banks County, GA', caption: 'Metering pumps and chemical feed tank' },
      { src: '/capabilities/treatment-alt.jpg', location: 'Banks County, GA', caption: 'Chemical tanks and feed system' },
      { src: '/capabilities/treatment-milligan-las.jpg', location: 'Milligan, TX', caption: 'Chemical feed and land application (LAS) system' },
      { src: '/capabilities/treatment-emporia-wqs.jpg', location: 'Emporia, KS', caption: 'MonoClor RCS water quality and residual control station' },
      { src: '/capabilities/treatment-milligan-tank.jpg', location: 'Milligan, TX', caption: 'Chemical feed and storage tank system' },
      { src: '/capabilities/treatment-banks-gallery.jpg', location: 'Banks County, GA', caption: 'Finished valve gallery and below-grade piping' },
    ],
    jobs: [
      { no: '9037', loc: 'Pataskala, OH' },
      { no: '7104', loc: 'Hot Springs, AR' },
      { no: '7015', loc: 'Yemassee, SC' },
    ],
    items: [
      'Reverse osmosis (RO) and membrane treatment system installation',
      'Chemical feed systems, metering pumps, and injection assemblies',
      'Sodium hypochlorite, chlorine, and disinfection systems',
      'Cartridge filters, pressure vessels, and pretreatment equipment',
      'Process piping for feed, permeate, concentrate, drain, and waste streams',
      'Chemical storage tanks, day tanks, and secondary containment systems',
      'Instrumentation, sample ports, and analyzer connections',
      'Equipment setting, anchorage, alignment, and structural supports',
      'Manufacturer-assisted startup and commissioning',
      'Functional testing and final system integration',
    ],
  },
  {
    key: 'pumps',
    icon: Gauge,
    title: 'Pump Stations & Booster Systems',
    markets: ['water', 'industrial'],
    desc: 'OMC constructs and equips municipal and industrial pump stations that move water reliably through distribution, treatment, and process systems. Our work includes the structural, mechanical, and underground infrastructure required to place pumping facilities into service.',
    images: [
      { src: '/capabilities/pumps-hero.jpg', location: 'Lexington-Leesville, SC', caption: 'Completed pump piping' },
      { src: '/capabilities/pumps-alt.jpg', location: 'Banks County, GA', caption: 'Twin booster pumps' },
      { src: '/capabilities/pumps-banks-boosters.jpg', location: 'Banks County, GA', caption: 'Twin split-case booster pumps' },
    ],
    jobs: [
      { no: '9035', loc: 'Lexington-Leesville, SC' },
      { no: '9053', loc: 'McKinney, TX' },
      { no: '8069', loc: 'Harleyville, SC' },
    ],
    items: [
      'Booster pump stations',
      'Lift stations and process pump stations',
      'Pump equipment installation and alignment',
      'Suction, discharge, and bypass piping',
      'Valve vaults and pump station piping headers',
      'Pump foundations and equipment pads',
      'Mechanical equipment supports and anchors',
      'Underground utility connections',
      'Hydrostatic testing and operational testing',
      'Final startup and system commissioning',
    ],
  },
  {
    key: 'piping',
    icon: Wrench,
    title: 'Process & Mechanical Piping',
    markets: ['water', 'industrial', 'energy'],
    desc: 'OMC installs process piping systems that transport water, chemicals, and industrial process fluids throughout treatment plants and industrial facilities. Every system is installed to engineered specifications and tested prior to turnover.',
    images: [
      { src: '/capabilities/piping-hero.jpg', location: 'Gaffney, SC', caption: 'Large-diameter pipe run' },
      { src: '/capabilities/piping-alt.jpg', location: 'Laurens, SC', caption: 'Pedestal mechanical piping' },
      { src: '/capabilities/piping-emporia-mechanical.jpg', location: 'Emporia, KS', caption: 'Mechanical process piping installation' },
      { src: '/capabilities/piping-emporia-ammonia.jpg', location: 'Emporia, KS', caption: 'Ammonia system process piping' },
      { src: '/capabilities/piping-lafayette.jpg', location: 'Lafayette, IN', caption: 'Mechanical process piping run' },
      { src: '/capabilities/piping-w-manifold.jpg', caption: 'Interior process pipe manifold' },
      { src: '/capabilities/piping-w-pedestals.jpg', caption: 'Large-diameter pipe on concrete pedestals' },
      { src: '/capabilities/piping-w-pumproom.jpg', caption: 'Pump station process piping' },
      { src: '/capabilities/piping-w-risers.jpg', caption: 'Process pipe risers and headers' },
    ],
    jobs: [
      { no: '9105', loc: 'Gaffney, SC' },
      { no: '9035', loc: 'Lexington-Leesville, SC' },
      { no: '3079', loc: 'Galveston, TX' },
    ],
    items: [
      'Carbon steel, stainless steel, ductile iron, PVC, HDPE, and specialty piping',
      'Process piping fabrication and installation',
      'Equipment tie-ins and mechanical connections',
      'Flanged, grooved, threaded, welded, and restrained joint systems',
      'Pipe supports, guides, and restraints',
      'Expansion joints and flexible connections',
      'Pressure testing and leak testing',
      'System flushing and cleaning',
      'Equipment integration',
      'Final commissioning support',
    ],
  },
  {
    key: 'valves',
    icon: SlidersHorizontal,
    title: 'Valves, Controls & Flow Management',
    markets: ['water', 'industrial'],
    desc: 'Reliable infrastructure depends on precise flow control. OMC installs the valves, metering equipment, and process components that regulate system performance throughout municipal and industrial facilities.',
    images: [
      { src: '/capabilities/valves-hero.jpg', location: 'Georgetown, TX', caption: 'Butterfly valve and couplings' },
      { src: '/capabilities/valves-alt.jpg', location: 'Lexington-Leesville, SC', caption: 'Valve assembly' },
      { src: '/capabilities/valves-w-gear.jpg', caption: 'Gear-operated valve assembly' },
      { src: '/capabilities/valves-w-manifold.jpg', caption: 'Valve manifold and flow control' },
    ],
    jobs: [
      { no: '9105', loc: 'Gaffney, SC' },
      { no: '3071', loc: 'Warren County, MS' },
      { no: '9128', loc: 'Georgetown, TX' },
    ],
    items: [
      'Butterfly, plug, gate, check, and control valve installation',
      'Flow meters and metering assemblies',
      'Pressure-reducing and pressure-sustaining valves',
      'Air release and vacuum valve assemblies',
      'Backflow prevention equipment',
      'Valve vault construction and equipment installation',
      'Actuated valve assemblies',
      'Instrumentation interfaces',
      'Functional testing and calibration support',
      'Final operational verification',
    ],
  },
  {
    key: 'underground',
    icon: Shovel,
    title: 'Underground Utilities',
    markets: ['water', 'industrial'],
    desc: 'OMC constructs underground utility systems that connect facilities to critical water, process, and site infrastructure. Our crews perform excavation, installation, testing, and restoration while maintaining strict grade and alignment tolerances.',
    images: [
      { src: '/capabilities/underground-hero.jpg', location: 'Marshall County, TN', caption: 'Large-diameter pipe bedding' },
      { src: '/capabilities/underground-alt.jpg', location: 'Eunice, LA', caption: 'Water main in trench' },
      { src: '/capabilities/underground-w-trench1.jpg', caption: 'Buried valve and pipe in trench' },
      { src: '/capabilities/underground-w-trench2.jpg', caption: 'Pipe installation in open trench' },
    ],
    jobs: [
      { no: '9111', loc: 'Hunt County, TX' },
      { no: '9067', loc: 'Kearney, MO' },
      { no: '4018', loc: 'Deer, AR' },
    ],
    items: [
      'Water transmission mains',
      'Distribution piping',
      'Process utility piping',
      'Storm drainage systems',
      'Utility crossings and service connections',
      'Trenching, bedding, and backfill',
      'Concrete encasement where specified',
      'Manholes, valve boxes, and utility structures',
      'Hydrostatic testing and disinfection',
      'Surface restoration and final grading',
    ],
  },
  {
    key: 'vaults',
    icon: Vault,
    title: 'Vaults & Below-Grade Structures',
    markets: ['water', 'industrial'],
    desc: 'OMC constructs reinforced concrete underground structures that protect critical utilities, equipment, and process systems while providing long-term durability and accessibility.',
    images: [
      { src: '/capabilities/vaults-hero.jpg', location: 'Bullard, TX', caption: 'Cast-in-place concrete vaults' },
      { src: '/capabilities/vaults-alt.jpg', location: 'Crestview, FL', caption: 'Below-grade formwork' },
      { src: '/capabilities/vaults-i-castwall.jpg', caption: 'Cast-in-place below-grade concrete wall' },
    ],
    jobs: [
      { no: '4041', loc: 'Bullard, TX' },
      { no: '7072', loc: 'Stillwater, OK' },
      { no: '9075', loc: 'Laurens, SC' },
    ],
    items: [
      'Reinforced concrete valve vaults',
      'Meter vaults and utility vaults',
      'Wet wells and dry wells',
      'Underground chambers',
      'Process pits and equipment pits',
      'Concrete trenches and utility corridors',
      'Pipe penetrations and embedded systems',
      'Structural waterproofing coordination',
      'Access hatches, ladders, and safety components',
      'Equipment installation and final integration',
    ],
  },
  {
    key: 'site',
    icon: Mountain,
    title: 'Civil Sitework',
    markets: ['water', 'industrial', 'energy'],
    desc: 'Every successful project begins with properly constructed site infrastructure. OMC prepares and develops project sites to support long-term structural performance and efficient facility operation.',
    images: [
      { src: '/capabilities/site-i-aerial-plant.jpg', caption: 'Aerial — tank mat foundation at industrial facility' },
      { src: '/capabilities/site-w-aerial-laydown.jpg', caption: 'Aerial — site development and pipe laydown' },
      { src: '/capabilities/site-w-aerial-foundation.jpg', caption: 'Aerial — completed tank foundation' },
      { src: '/capabilities/site-i-massexcav.jpg', caption: 'Mass excavation and compaction' },
      { src: '/capabilities/site-w-earthwork.jpg', caption: 'Site grading and earthwork' },
      { src: '/capabilities/site-w-pour.jpg', caption: 'Tank foundation concrete placement' },
      { src: '/capabilities/site-w-excavation.jpg', caption: 'Foundation excavation' },
      { src: '/capabilities/site-i-spheres.jpg', caption: 'Pier-supported foundation, industrial storage spheres' },
    ],
    jobs: [
      { no: '9100', loc: 'Choctaw, MS' },
      { no: '9138', loc: 'Lavon, TX' },
      { loc: 'Ash, NC' },
    ],
    items: [
      'Mass excavation and earthwork',
      'Site grading and finish grading',
      'Structural fill placement and compaction',
      'Subgrade stabilization',
      'Storm drainage installation',
      'Culverts and drainage structures',
      'Access roads and construction entrances',
      'Crane pads and equipment laydown areas',
      'Erosion and sediment control',
      'Site restoration and stabilization',
    ],
  },
  {
    key: 'startup',
    icon: ClipboardCheck,
    title: 'Testing, Commissioning & System Integration',
    markets: ['water', 'industrial', 'energy'],
    desc: 'OMC delivers complete project startup support by verifying that every installed system performs safely and reliably before final turnover. Our commissioning process helps transition construction into fully operational infrastructure.',
    images: [
      { src: '/capabilities/startup-hero.jpg', location: 'Summerton, SC', caption: 'Metering and integrated piping' },
    ],
    jobs: [
      { no: '9096', loc: 'Pensacola, FL' },
      { no: '9072', loc: 'Corunna, MI' },
      { no: '9138', loc: 'Lavon, TX' },
    ],
    items: [
      'Hydrostatic testing',
      'Pressure testing',
      'Leak testing',
      'System flushing and cleaning',
      'Water main disinfection',
      'Equipment startup support',
      'Manufacturer coordination',
      'Functional performance testing',
      'Punch list completion and owner training support',
      'Final project turnover documentation',
    ],
  },
  {
    key: 'enclosures',
    icon: Warehouse,
    title: 'FRP Structures & Equipment Enclosures',
    markets: ['water', 'industrial'],
    desc: 'OMC installs fiberglass-reinforced plastic (FRP) structures and protective enclosures used to house process equipment, valves, instrumentation, and chemical systems. These corrosion-resistant systems provide long-term durability in demanding municipal and industrial environments.',
    images: [
      { src: '/capabilities/enclosures-hero.jpg', location: 'Summerton, SC', caption: 'FRP equipment enclosure' },
      { src: '/capabilities/enclosures-alt.jpg', location: 'Summerton, SC', caption: 'Enclosure interior equipment' },
      { src: '/capabilities/enclosures-lafayette-buildout.jpg', location: 'Lafayette, IN', caption: 'Interior equipment buildout' },
      { src: '/capabilities/enclosures-emporia-buildout.jpg', location: 'Emporia, KS', caption: 'Equipment buildout and coiling-door enclosure' },
    ],
    jobs: [
      { no: '9100', loc: 'Choctaw, MS' },
      { no: '9065', loc: 'Laurens County, SC' },
      { no: '7111', loc: 'Fort Meade County, MD' },
    ],
    items: [
      'FRP equipment enclosure installation',
      'Chemical storage and process enclosures',
      'Valve and instrumentation buildings',
      'Pre-engineered fiberglass structures',
      'Equipment pads and anchorage systems',
      'Utility penetrations and piping interfaces',
      'Ventilation and access coordination',
      'Foundation and structural integration',
      'Equipment setting and final assembly',
      'Startup coordination and project turnover',
    ],
  },
];
